import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { meterRadioHeadsApi } from '../../api/meterRadioHeads';

function apiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
}

function formatWhen(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch {
    return String(value);
  }
}

export default function MeterRadioHeadPanel({
  open,
  device,
  buildingId,
  onClose,
  onChanged,
  embedded = false,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [state, setState] = useState(null);
  const [form, setForm] = useState({
    serial_number: '',
    dev_eui: '',
    manufacturer: '',
    model: '',
    baseline_meter_value: device?.last_data ?? '',
    notes: '',
  });
  const [closeReason, setCloseReason] = useState('');

  const load = useCallback(async () => {
    if (!device?.id) return;
    setLoading(true);
    setError('');
    try {
      const next = await meterRadioHeadsApi.getDeviceState(String(device.id));
      setState(next);
      const active = next.active_binding;
      if (active?.head) {
        setForm((prev) => ({
          ...prev,
          serial_number: active.head.serial_number || '',
          dev_eui: active.head.dev_eui || '',
          manufacturer: active.head.manufacturer || '',
          model: active.head.model || '',
          baseline_meter_value:
            active.baseline_meter_value != null
              ? String(active.baseline_meter_value)
              : device?.last_data ?? '',
        }));
      } else {
        setForm((prev) => ({
          ...prev,
          serial_number: '',
          dev_eui: device?.dev_eui || '',
          manufacturer: '',
          model: '',
          baseline_meter_value: device?.last_data ?? '',
        }));
      }
    } catch (err) {
      setError(apiError(err, 'Не удалось загрузить накладку'));
    } finally {
      setLoading(false);
    }
  }, [device?.id, device?.dev_eui, device?.last_data]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleBind = async () => {
    if (!device?.id) return;
    if (!form.dev_eui.trim()) {
      setError('Укажите DevEUI накладки');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await meterRadioHeadsApi.bind(String(device.id), {
        head: {
          serial_number: form.serial_number.trim() || null,
          dev_eui: form.dev_eui.trim(),
          manufacturer: form.manufacturer.trim() || null,
          model: form.model.trim() || null,
          building_id: buildingId || null,
          notes: form.notes.trim() || null,
        },
        baseline_meter_value:
          form.baseline_meter_value === '' || form.baseline_meter_value == null
            ? null
            : Number(form.baseline_meter_value),
        notes: form.notes.trim() || null,
      });
      await load();
      onChanged?.();
    } catch (err) {
      setError(apiError(err, 'Не удалось привязать накладку'));
    } finally {
      setSaving(false);
    }
  };

  const handleUnbind = async () => {
    if (!device?.id) return;
    setSaving(true);
    setError('');
    try {
      await meterRadioHeadsApi.unbind(String(device.id), closeReason.trim() || 'unbound');
      setCloseReason('');
      await load();
      onChanged?.();
    } catch (err) {
      setError(apiError(err, 'Не удалось отвязать накладку'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: 220, display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const active = state?.active_binding;
  const history = state?.history || [];

  return (
    <Stack spacing={1.5}>
      {error ? (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Alert severity="info" sx={{ borderRadius: 2 }}>
        Накладка (голова) — один радиомодуль на один счётчик ХВС/ГВС. Без каналов.
        Нельзя совмещать с импульсным регистратором.
      </Alert>

      {state?.has_pulse_connection ? (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Прибор сейчас на импульсном канале. Снимите канал регистратора, чтобы поставить накладку.
        </Alert>
      ) : null}

      {active?.head ? (
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          Активная накладка: DevEUI {active.head.dev_eui}
          {active.head.serial_number ? ` · № ${active.head.serial_number}` : ''}
          {active.baseline_meter_value != null
            ? ` · начальное ${active.baseline_meter_value}`
            : ''}
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Активной накладки нет.
        </Typography>
      )}

      <Stack spacing={1.25}>
        <TextField
          size="small"
          label="DevEUI накладки"
          value={form.dev_eui}
          onChange={(e) => setForm((prev) => ({ ...prev, dev_eui: e.target.value }))}
          disabled={Boolean(state?.has_pulse_connection)}
          required
        />
        <TextField
          size="small"
          label="Серийный номер накладки"
          value={form.serial_number}
          onChange={(e) => setForm((prev) => ({ ...prev, serial_number: e.target.value }))}
          disabled={Boolean(state?.has_pulse_connection)}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            size="small"
            label="Производитель"
            value={form.manufacturer}
            onChange={(e) => setForm((prev) => ({ ...prev, manufacturer: e.target.value }))}
            disabled={Boolean(state?.has_pulse_connection)}
            fullWidth
          />
          <TextField
            size="small"
            label="Модель"
            value={form.model}
            onChange={(e) => setForm((prev) => ({ ...prev, model: e.target.value }))}
            disabled={Boolean(state?.has_pulse_connection)}
            fullWidth
          />
        </Stack>
        <TextField
          size="small"
          label="Начальное значение счётчика при установке"
          value={form.baseline_meter_value}
          onChange={(e) => setForm((prev) => ({ ...prev, baseline_meter_value: e.target.value }))}
          disabled={Boolean(state?.has_pulse_connection)}
          helperText="Показание прибора в момент монтажа накладки"
        />
        <TextField
          size="small"
          label="Комментарий"
          value={form.notes}
          onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
          disabled={Boolean(state?.has_pulse_connection)}
        />
      </Stack>

      <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
        {!embedded ? (
          <Button onClick={onClose} disabled={saving}>
            Закрыть
          </Button>
        ) : null}
        {active ? (
          <>
            <TextField
              size="small"
              label="Причина отвязки"
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <Button color="warning" variant="outlined" disabled={saving} onClick={handleUnbind}>
              Отвязать
            </Button>
          </>
        ) : null}
        <Button
          variant="contained"
          disabled={saving || state?.has_pulse_connection || !form.dev_eui.trim()}
          onClick={handleBind}
        >
          {saving ? 'Сохранение…' : active ? 'Заменить накладку' : 'Привязать накладку'}
        </Button>
      </Stack>

      {history.length > 0 ? (
        <>
          <Divider />
          <Typography variant="subtitle2">История накладок</Typography>
          <List dense sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #E2E8F0' }}>
            {history.map((item) => (
              <ListItem key={item.id} divider>
                <ListItemText
                  primary={`${item.head?.dev_eui || item.head_id}${
                    item.active_to ? ' · закрыта' : ' · активна'
                  }`}
                  secondary={
                    <>
                      {item.head?.serial_number ? `№ ${item.head.serial_number} · ` : ''}
                      с {formatWhen(item.active_from)}
                      {item.active_to ? ` по ${formatWhen(item.active_to)}` : ''}
                      {item.baseline_meter_value != null
                        ? ` · начальное ${item.baseline_meter_value}`
                        : ''}
                      {item.close_reason ? ` · ${item.close_reason}` : ''}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </>
      ) : null}
    </Stack>
  );
}
